import { Link } from "../../components"
import {
  decisiontreemale,
  decisiontreefemale,
  decisiontreefull,
} from "../../assets/images"

export const textData = [
  {
    type: "text",
    content: (
      <>
        Rotterdam isn’t the only city using algorithms to interrogate welfare
        recipients. But it is, to date, the most transparent about what it’s
        doing. Rotterdam was chosen for this story not because its system is
        especially novel, but because out of dozens of cities we contacted, it
        was the only one willing to share the code behind its algorithm.
        Alongside this, the city also handed over the list of variables powering
        it, evaluations of the model’s performance, and the handbook used by its
        data scientists. And when faced with the prospect of potential court
        action under freedom-of-information laws—European equivalents to US
        sunshine laws—it also shared the machine learning model itself,
        providing unprecedented access.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        This transparency revealed a system unfit for its intended purpose. In
        2017, Accenture, the city’s technology partner on an initial version of
        the project, promised that “advanced analytics” would be combined with
        machine learning to create “unbiased citizen outcomes.” In a
        presentation produced at the time, the consulting firm claimed such a
        system would help Rotterdam ensure that people in need of help received
        it and that there would be “a fair distribution of welfare.”
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        The reality of Rotterdam’s suspicion machine is starkly different. This
        is an algorithm that fails the city’s own test of fairness—and despite
        being touted by Accenture as an “ethical solution,” it’s so opaque that
        it denies due process to the people caught up in it.
      </>
    ),
  },
  {
    type: "blockquote",
    content: (
      <>
        “We decided to give you maximum insight into the model, not only because
        of our desire to be an open and transparent organization but also to be
        able to learn from the insights of others.”
        <br />
        <span>
          &mdash;&nbsp; Annemarie de Rotte, Rotterdam's director of income
        </span>
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        “We delivered a startup model and transitioned it to the city as our
        contract ended in 2018,” says Accenture spokesperson Chinedu Udezue. The
        company declined to comment on the historical lack of transparency
        around the algorithm’s use.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        In the real world, thousands of people like Sara and Yusef have suffered
        the consequences of a flawed algorithm based on flawed data. People
        flagged by the algorithm as potential fraudsters can, in theory, ask for
        all the data the city has collected on them. They can ask for their risk
        score. They can ask for their rank. They can try to ask a city
        caseworker, a lawyer, or a judge how the suspicion machine decided that
        those numbers amount to a pattern of illegal behavior. Until now, they
        have had no way of making sense of those answers.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Rotterdam defends its right to enforce the law but says our
        investigation has shown the need for greater transparency wherever
        similar systems are deployed. “[We] consider it very important that
        other governments and organizations are aware of the risks of using
        algorithms,” says Rotterdam’s de Rotte. “We decided to give you maximum
        insight into the model, not only because of our desire to be an open and
        transparent organization but also to be able to learn from the insights
        of others.”
      </>
    ),
  },
]

export const beforeDistributionChart = [
  {
    type: "text",
    content: (
      <>
        Rotterdam's welfare-fraud algorithm is trained on millions of data
        points, but it is blind to what is actually happening in the real world.
        In reality, different people commit different kinds of violations—from
        honest mistakes when filling out a form to organized crime. Rotterdam
        itself says that many legal violations stem from a law that requires
        small changes in a beneficiary’s living situation and finances to be
        reported immediately through a process many beneficiaries do not
        understand.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        But the algorithm learns to make its predictions based on a pattern it
        extrapolates from the training data that collapses honest mistakes and
        deliberate fraud into one category: fraud. It tries to find
        commonalities between people making paperwork mistakes and people
        deliberately trying to cheat the system. The consequence is that it is
        not very good at predicting either. “The algorithm converts ‘honest
        mistakes’ into learned associations between poor Dutch skills and
        propensity for fraud, allowing welfare officials to claim that migrants
        who do not speak Dutch are scientifically untrustworthy,” says Dressel.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Flaws in the training data and design of the algorithm combine to create
        a system that is alarmingly inaccurate, experts claim. “It's not useful
        in the real world,” says Margaret Mitchell, chief ethics scientist at AI
        firm Hugging Face and a former AI ethicist at Google. According to
        Mitchell, Rotterdam’s own performance evaluation indicates the model is
        “essentially random guessing.”
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Assessing an algorithm means looking not just at whether it is accurate
        but also whether it discriminates. Was Sara alone in being scored higher
        for being a single mother, or were single mothers as a group getting
        higher scores? Was it just Yusef's poor Dutch that made him suspicious,
        or were most poor speakers of Dutch getting higher scores? It's possible
        to work out whether the algorithm discriminated against certain groups
        based on Rotterdam's own internal definition of discrimination. The code
        for the city’s risk-scoring algorithm includes a test for whether people
        of a specific gender, age, neighborhood, or relationship status are
        flagged at higher rates than other groups.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        De Rotte, Rotterdam’s director of income, says the city never actually
        ran this particular code, but it did run similar tests to see whether
        certain groups were overrepresented or underrepresented among the
        highest-risk individuals and found that they were. We ran a similar test
        to Rotterdam’s and found widespread discrimination against several
        vulnerable groups. In other words, it isn’t just Sara and Yusef being
        discriminated against, it’s also people like them.
      </>
    ),
  },
]

export const afterDistributionChart = [
  {
    type: "text",
    content: (
      <>
        Language has both a large influence on people’s score and is the most
        obvious proxy for ethnicity used by the algorithm. Rotterdam's algorithm
        tries to work out how well someone can speak Dutch based on 20
        variables. It’s fed data about how well someone can speak, write, and
        understand Dutch, along with the number of appointments they've had with
        a caseworker about the language requirement. If someone is rated as
        struggling with Dutch across these variables, they are two times more
        likely to be flagged for a welfare fraud investigation than somebody who
        is fluent in Dutch. Assigning high risk scores based on Dutch fluency
        means that people who are non-white immigrants are more likely to be
        investigated. This type of ethnic discrimination by algorithms has been
        dubbed a{" "}
        <Link href="https://nymag.com/intelligencer/2018/06/how-predpol-and-nypd-create-digital-stop-and-frisk.html">
          digital stop and frisk
        </Link>
        .
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        <Link href="https://www.documentcloud.org/documents/23694970-model-evaluation">
          Rotterdam’s internal evaluation documents
        </Link>{" "}
        reveal that its risk-scoring system is so inaccurate that, according to
        some metrics, it does little better than random sampling. Our experiment
        also reveals that it discriminates against vulnerable people by scoring
        them more highly as a result of their vulnerabilities, such as having
        children or struggling financially. But do women, people who do not
        speak Dutch, or parents commit welfare fraud more often than people in
        other groups?
      </>
    ),
  },
  {
    type: "blockquote",
    content: (
      <>
        "Models will always be poor estimators of actual risk." <br />
        <span>
          &mdash;&nbsp; Margaret Mitchell, chief ethics scientist, Hugging Face
        </span>
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        To know that, Rotterdam needs to share more information. We requested
        data from the city that would allow us to see whether investigators had
        found more cases of fraud among specific groups, but officials declined
        to provide us with that detail on unspecified legal grounds. For now,
        only Rotterdam knows whether the vulnerable groups its algorithm found
        suspicious really did break the law more often than other groups.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Based on its own assessment, Rotterdam knew who was getting flagged at
        higher rates and removed some proxy variables, but kept using the system
        until finally suspending the algorithm’s use in late 2021. Yet that same
        year, Rotterdam city councillor Richard Moti insisted there was “no bias
        in both the input and output” of the algorithm. But no matter what
        adjustments are made, a mathematical equation can do only so much to
        accurately spot fraud in the messy reality of how people live. “Models
        will always be poor estimators of actual risk,” says Mitchell, adding
        that algorithms are likely to exclude potentially important personal
        details. “If you took situations case by case, the predictions made by a
        mathematical model would never capture all of the variables at play in
        each individual case.”
      </>
    ),
  },
]

export const beforeDecisionTree = [
  {
    type: "text",
    content: (
      <>
        From face recognition software that{" "}
        <Link href="https://sitn.hms.harvard.edu/flash/2020/racial-discrimination-in-face-recognition-technology/">
          performs poorly on people of color
        </Link>{" "}
        to hiring algorithms that{" "}
        <Link href="https://www.reuters.com/article/us-amazon-com-jobs-automation-insight-idUSKCN1MK08G">
          screen out women
        </Link>
        , poor training data can create machine learning models that perpetuate
        biases. And the Rotterdam training data is seemingly no different.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        It contained information on 12,707 people who had previously been
        investigated by the city, half of whom were found to have broken the
        law. From this data, the algorithm tries to work out what distinguishes
        someone who is committing welfare fraud from someone who is not. But an
        algorithm is only as good as the data it is trained on—and Rotterdam’s
        data was flawed.
      </>
    ),
  },

  {
    type: "text",
    content: (
      <>
        Say, as a theoretical example, most men in the training data were
        selected at random, while most women were investigated following tips
        from neighbors or hunches from caseworkers. In this scenario, the data
        might show that women on welfare are much more likely to commit fraud
        than men, without considering whether women, generally, are more likely
        to commit fraud. Without seeing the full picture, the algorithm would
        learn to associate women with fraud.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Rotterdam uses random selection, anonymous tips, and category checks
        that change every year (checking all men in a particular zip code, for
        example) to start investigations, with data from all three approaches
        used to train the algorithm. Rotterdam’s de Rotte declined to provide
        detailed information on how individuals in the training data were
        selected for investigation. Although the city was running the algorithm,
        it continued using random selection and category checks alongside risk
        scores to choose who to investigate and what to feed into the training
        data. “If a training data set [contains] systemic biases, then the
        algorithmic output will always re-create these inequalities,” says Julia
        Dressel, an expert in the use of technology in criminal justice systems.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Some groups barely show up in Rotterdam’s training data. There should
        have been around 880 young people in the data to reflect the actual
        proportion of people under the age of 27 who receive welfare in the
        city. Instead, there were only 52. In our tests of the algorithm, it was
        age, specifically youth, that was the most significant attribute in
        raising risk scores. To the algorithm, it appeared as if young people
        are more likely to commit welfare fraud, but it drew that conclusion
        based on a sample size so small it was effectively useless.
      </>
    ),
  },
]

export const afterDecisionTree = [
  {
    type: "text",
    content: (
      <>
        A key lesson about the Rotterdam algorithm is that Sara's scoring is
        entirely unique. Change gender from male to female for another person in
        the data, their rank may shift by hundreds of places, thousands, not at
        all, or he may even move down the list. If you take another woman and
        give her children, again, the outcome may be entirely different than the
        changes we see to Sara's score. The impact of variables can be very
        large for some and very small for others. Until now, the mystery was how
        the suspicion machine judges each person's unique set of
        characteristics.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Sara and Yusef and the thousands like them will likely never see their
        own data. They probably will not know their score. They will not know
        their rank. They may not even know that an algorithm is the reason why
        an investigator showed up at their door, let alone how the algorithm
        works.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Margaret Mitchell, a leading authority on Machine Learning, said the
        Rotterdam risk-scoring system developers do not have enough information
        to appropriately model the kinds of nuances that they'd be seeing in the
        real world. “By and large, you have a recipe for a model that can't
        generalise well to the real world, based on what it's learned, meaning
        it's not useful in the real world.”
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        According to Mitchell, Rotterdam’s own performance metrics indicate the
        model is “essentially random guessing.” She added that it clearly would
        receive a failing grade on meet performance standards necessary to
        responsibly deploy it in the real world. “If you have a system that
        scores lower than a B, on the tests you give it, it would generally be
        considered not ready for deployment, if for no other reason than the bad
        reputational cost of the company.”
      </>
    ),
  },
]

export const afterCaseCardSara = [
  {
    type: "text",
    content: (
      <>
        Discriminating against people based on their gender and ethnic
        background is illegal in the Netherlands, but converting that
        information into data points to be fed into an algorithm may not be.
        This gray area, in part the result of{" "}
        <Link href="https://wetten.overheid.nl/BWBR0013060/2023-01-01">
          broad legal leeway
        </Link>{" "}
        granted in the name of fighting welfare fraud, lets officials process
        and profile welfare recipients based on sensitive characteristics in
        ways that would otherwise be illegal. Dutch courts are currently
        reviewing the issue.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Rotterdam's algorithm doesn't explicitly include race, ethnicity, or
        place of birth among the 315 attributes, or variables, it uses to
        calculate someone’s risk score. More than half of the city’s residents
        are first- or second-generation migrants. Some of those variables can be
        proxies for ethnicity.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Rotterdam’s director of income, Annemarie de Rotte, says the city
        attempted to remove some proxy variables related to ethnicity following
        the external audit, but some remained in the system. For example,
        everyone who gets welfare in the Netherlands is{" "}
        <Link href="https://www.rijksoverheid.nl/onderwerpen/bijstand/vraag-en-antwoord/wat-is-de-taaleis-in-de-bijstand">
          required
        </Link>{" "}
        to speak Dutch or be able to prove they are making an effort to do so.
        This language requirement, as well as someone having a native language
        other than Dutch, can be proxies for ethnicity. Having financial
        problems and multiple roommates can also be proxies for poverty. This is
        how proxies work in the case of Yusef.
      </>
    ),
  },
]

export const afterCaseCardYusef = [
  {
    type: "text",
    content: (
      <>
        The Dutch government{" "}
        <Link href="https://open.overheid.nl/documenten/ronl-c409ea31-2c00-4318-9a45-d47ad8a2ca7f/pdf">
          recognizes
        </Link>{" "}
        that language skills can be a proxy for ethnicity. In its analysis of
        the variables used by the Rotterdam algorithm, the Netherlands Court of
        Audit, an independent body appointed by the Netherlands’ government,
        pointed to “fluency in the Dutch language” as an example of a variable
        that could “result in unwanted discrimination.” The algorithm also
        judges people based on their native language, with different languages
        given different numbers, each with a different impact on someone’s final
        risk score. When asked what a “0” meant for this variable, which is what
        half the people in the data have for this field, de Rotte says the city
        does not know.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        Risk-score algorithms, like the one used in Rotterdam, are often accused
        of encoding human bias—but proving that is difficult. Once you have the
        code and the data that powers it, however, you can start to unpack how
        this process works. For example, Rotterdam’s algorithm has 54 variables
        based on subjective assessments made by caseworkers. This amounts to 17
        percent of total variables, each with varying impacts on the score.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        The use of these subjective assessments raises ethical red flags for
        many academics and human rights advocates. “Even when the use of the
        variable does not lead to a higher risk score, the fact that it was used
        to select people is enough to conclude that discrimination has
        occurred,” says Tamilla Abdul-Aliyeva, a researcher on technology and
        human rights at Amnesty International.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        And it’s not just the inclusion of subjective assessments that is cause
        for concern—such systems also take nuanced information and flatten it.
        The comment field in the Rotterdam system, for example, where
        caseworkers are asked to make general observations, is binary. Any
        comment is converted to a "1," while a blank field is converted to a
        "0." This means negative and positive comments affect the score in the
        same way. If, for example, a caseworker adds a comment that reads “shows
        no desire to achieve results,” it has the same effect on the risk score
        as a comment that reads “shows desire to achieve results.” The{" "}
        <Link href="https://drive.google.com/file/d/1JIml72_mHBZyl0q8Y1FkpWCdX7vgMeFW/view?usp=sharing">
          manual
        </Link>{" "}
        given to caseworkers for evaluating beneficiaries makes no mention of
        their notes being fed into the risk-scoring algorithm, nor that positive
        and negative comments will be read the same.
      </>
    ),
  },
]

export const afterCaseCardSwitchSara = [
  {
    type: "text",
    content: (
      <>
        To the algorithm, Sara differs from the average male based on just eight
        of the 315 variables each person is judged on, yet she is nearly three
        times more likely to be flagged as a potential fraudster. The gap in
        their two scores, despite them having data that mostly overlaps, stems
        from how the machine makes links between certain traits.
      </>
    ),
  },
]

export const decisionTreeText = [
  {
    type: "text",
    content: (
      <>
        Data scientists build risk models to scour large amounts of data to
        uncover hidden patterns. While the machine learning algorithm used in
        Rotterdam’s system, called a gradient boosting machine, relied on
        complex mathematics to uncover these patterns, its basic building block
        is a decision tree.
      </>
    ),
  },
  {
    type: "image",
    src: decisiontreefull,
    alt: "A decision tree that starts with the question 'Are you female?' and shows how that can lead to other branching questions",
    caption: ``,
  },
  {
    type: "text",
    content: (
      <>
        Rotterdam’s algorithm is made up of 500 decision trees. These trees
        categorize individuals based on a series of yes-no questions. At each
        question, a person can travel down the left or right branch until they
        reach the bottom of the tree. With no questions left, the decision tree
        gives that person a value. The values of all the 500 trees powering
        Rotterdam’s algorithm are combined to calculate a person’s risk score.
      </>
    ),
  },
  {
    type: "text",
    content: (
      <>
        The real decision trees in the model contain many variables and
        branches. To understand how Sara and Yusef’s scores are generated, Sara
        is going to travel down a simplified decision tree. Sara is the mother
        of two children. She recently moved into a new apartment after splitting
        up with her long-term partner. You can see how these attributes send
        Sara down a specific branch of a decision tree.
      </>
    ),
  },
  {
    type: "image",
    src: decisiontreefemale,
    alt: "A decision tree that shows that women are more likely to be flagged for fraud",
    caption: ``,
  },

  {
    type: "text",
    content: (
      <>
        But what happens if Sara is male rather than female? As a woman, Sara is
        asked about her relationship status, children, and living situation. As
        a man, Sara is sent down an entirely different branch with entirely
        different questions. Two people, two different paths, and two different
        sets of questions. But we changed just one attribute: gender.
      </>
    ),
  },
  {
    type: "image",
    src: decisiontreemale,
    alt: "A decision tree that shows that men are less likely to be flagged for fraud",
    caption: ``,
  },
  {
    type: "text",
    content: (
      <>
        Using decision trees makes it hard to understand how any single variable
        affects how people are scored. Take gender in the example above: Men and
        women move down separate branches of the decision tree, and different
        variables are used to assess their risk. Men, for example, may be
        evaluated for their language skills and financial standing, while women
        could be scored on whether they have children or if they are in a
        relationship. As a result, being male may increase the risk score for
        some and decrease it for others.
      </>
    ),
  },
]
